import React from 'react';
import { UploadElement } from 'is24-corecss';
import { IconS24CrossCircle24Filled, IconS24DocumentPlus24 } from '@is24/cosma-ui-icons';
import type { AttachedFile } from '../fileUpload/FileUpload';

interface AttachedFilesProps {
  files: AttachedFile[];
  ariaLabel: string;
  removeLabel: string;
  onRemove: (id: string) => void;
}

interface AttachedFileRowProps {
  fileName: string;
  validationErrorMessage?: string;
  removeLabel: string;
  onRemove: () => void;
}

const splitFileName = (fileName: string): { baseName: string; extension: string } => {
  const extensionStart = fileName.lastIndexOf('.');

  if (extensionStart <= 0 || extensionStart === fileName.length - 1) {
    return { baseName: fileName, extension: '' };
  }

  return {
    baseName: fileName.slice(0, extensionStart),
    extension: fileName.slice(extensionStart),
  };
};

const AttachedFileRow: React.FC<AttachedFileRowProps> = ({
  fileName,
  validationErrorMessage,
  removeLabel,
  onRemove,
}) => {
  const { baseName, extension } = splitFileName(fileName);
  const hasValidationError = !!validationErrorMessage;

  return (
    <div
      className='border border-solid padding-m display-flex align-items-start justify-content-space-between'
      style={{
        backgroundColor: hasValidationError
          ? 'var(--color-background-danger-weak)'
          : 'var(--color-background-success-weak)',
        borderColor: hasValidationError ? 'var(--color-border-danger)' : 'transparent',
        borderRadius: 'var(--border-radius-300)',
      }}
    >
      <div className='display-flex align-items-start overflow-hidden'>
        <div
          className='margin-right-s display-flex align-items-center justify-content-center'
          style={{ color: hasValidationError ? 'var(--color-icon-danger)' : undefined }}
        >
          <IconS24DocumentPlus24 />
        </div>
        <div className='overflow-hidden'>
          <div className='display-flex align-items-center overflow-hidden'>
            <span className='font-ellipsis overflow-hidden' title={fileName}>
              {baseName}
            </span>
            {extension && <span className='font-nowrap'>{extension}</span>}
          </div>
          {validationErrorMessage && (
            <div className='font-body-small-bold margin-top-xs' style={{ color: 'var(--color-text-danger)' }}>
              {validationErrorMessage}
            </div>
          )}
        </div>
      </div>

      <UploadElement.Action
        onClickCapture={(event) => {
          event.preventDefault();
          onRemove();
        }}
      >
        <button
          type='button'
          aria-label={removeLabel}
          className='border-none padding-none cursor-pointer display-flex align-items-center justify-content-center'
        >
          <IconS24CrossCircle24Filled />
        </button>
      </UploadElement.Action>
    </div>
  );
};

const AttachedFiles: React.FC<AttachedFilesProps> = ({ files, ariaLabel, removeLabel, onRemove }) => (
  <div className='grid grid-flex margin-top-s' aria-label={ariaLabel}>
    {files.map(({ id, file, validationErrorMessage }) => (
      <div key={id} className='grid-item one-whole margin-bottom-s'>
        <AttachedFileRow
          fileName={file.name}
          validationErrorMessage={validationErrorMessage}
          removeLabel={removeLabel}
          onRemove={() => onRemove(id)}
        />
      </div>
    ))}
  </div>
);

export default AttachedFiles;
